// utils/listProcessor.js

// Helper function to generate list ID
  const generateListId = (questionNumber, type) => {
    // Clean the question number to make it a valid ID
    const cleanQuestionNumber = questionNumber.replace(/[^a-zA-Z0-9_]/g, "_");
    const typeSuffix =
      type === "rows"
        ? "_rows"
        : type === "columns"
        ? "_columns"
        : "_statements";

    return `${cleanQuestionNumber}${typeSuffix}`;
  };

// Helper function to process each question type
const processQuestionType = (items, questionNumber, type, listMap, onUpdate) => {
  const content = JSON.stringify(items);
  const existingList = listMap.get(content);

  if (existingList) {
    const questionExists = existingList.questionNumbers.some(
      q => q.name === questionNumber && q.type === type
    );
    
    if (!questionExists) {
      existingList.questionNumbers.push({
        name: questionNumber,
        type: type,
      });
      if (existingList.isManual && existingList.questionNumbers.length > 1) {
        existingList.isManual = false;
      }
      onUpdate();
    }
  } else {
    const tempKey = `${questionNumber}[${type}]`;
    listMap.set(content, {
      listKey: tempKey,
      listData: items,
      questionNumbers: [{
        name: questionNumber,
        type: type,
      }],
      primaryType: type,
      id: generateListId(questionNumber, type),
      isPotential: true
    });
  }
};

// Main processing function
export const processLists = (sdataGroup, existingLists = []) => {
  if (!sdataGroup || Object.keys(sdataGroup).length === 0) {
    throw new Error("No survey data found to create lists from!");
  }
  
  let listsCreated = 0;
  let listsUpdated = 0;
  const listMap = new Map();
  
  // FIRST: Add all existing lists to the map
  existingLists.forEach((existingList) => {
    const key = Object.keys(existingList)[0];
    const listData = existingList[key];
    const contentHash = JSON.stringify(listData);

    if (!listMap.has(contentHash)) {
      const existingQuestionNumbers = existingList.questionNumbers || [
        existingList.questionNumber ||
          key.replace(/Rows|Columns|Statements$/, ""),
      ];
      
      const questionNumbers = existingQuestionNumbers.map(num => {
        if (typeof num === 'object') {
          return num;
        } else {
          return {
            name: String(num),
            type: existingList.type || "rows"
          };
        }
      });

      listMap.set(contentHash, {
        listKey: key,
        listData: listData,
        questionNumbers: questionNumbers,
        primaryType: existingList.type || "rows",
        id: existingList.id || generateListId(questionNumbers[0]?.name || "unknown", existingList.type || "rows"),
        isManual: true
      });
    }
  });
  
  // SECOND: Process all questions in sdataGroup
  Object.entries(sdataGroup).forEach(([questionKey, questionData]) => {
    const questionNumber = questionData.Question_number || questionData.questionNumber || questionKey;
    
    // Process rows
    if (questionData.rows && questionData.rows.length > 0) {
      const commonRows = questionData.rows.filter(
        (item) => !item.exclusive && !item.other
      );
      
      if (commonRows.length > 1) {
        processQuestionType(
          commonRows, 
          questionNumber, 
          "rows", 
          listMap, 
          () => listsUpdated++
        );
      }
    }
    
    // Process columns
    if (questionData.columns && questionData.columns.length > 0) {
      const commonColumns = questionData.columns.filter(
        (item) => !item.exclusive && !item.other
      );
      
      if (commonColumns.length > 1) {
        processQuestionType(
          commonColumns, 
          questionNumber, 
          "columns", 
          listMap, 
          () => listsUpdated++
        );
      }
    }
      // Process values
    if (questionData.values && questionData.values.length > 0) {
      const commonColumns = questionData.values.filter(
        (item) => !item.exclusive && !item.other
      );
      
      if (commonColumns.length > 1) {
        processQuestionType(
          commonColumns, 
          questionNumber, 
          "values", 
          listMap, 
          () => listsUpdated++
        );
      }
    }
    // Process statements
    if (questionData.statements && questionData.statements.length > 0) {
      const commonStatements = questionData.statements.filter(
        (item) => !item.exclusive && !item.other
      );
      
      if (commonStatements.length > 1) {
        processQuestionType(
          commonStatements, 
          questionNumber, 
          "statements", 
          listMap, 
          () => listsUpdated++
        );
      }
    }
  });
  
  // THIRD: Convert map back to list format
  const allLists = Array.from(listMap.values()).map((listInfo) => {
    const questionNumbers = listInfo.questionNumbers;
    
    // Sort question names alphabetically
    const sortedQuestionNames = [...new Set(questionNumbers.map(q => q.name))].sort();
    
    // Get unique types
    const uniqueTypes = [...new Set(questionNumbers.map(q => q.type))];
    
    // Generate key
    let baseKey;
    if (sortedQuestionNames.length > 1) {
      const typeIdentifier = uniqueTypes.length > 1 ? 
        `[Mixed:${uniqueTypes.join("/")}]` : 
        `[${uniqueTypes[0]}]`;
      baseKey = `${sortedQuestionNames.join("_")}${typeIdentifier}`;
    } else {
      if (listInfo.listKey && !listInfo.isPotential) {
        baseKey = listInfo.listKey;
      } else {
        baseKey = `${sortedQuestionNames[0]}[${listInfo.primaryType}]`;
      }
    }
    
    // Track if this list was newly created
    const wasNewlyCreated = listInfo.isPotential && questionNumbers.length > 1;
    if (wasNewlyCreated) {
      listsCreated++;
    }
    
    return {
      [baseKey]: listInfo.listData,
      type: listInfo.primaryType,
      questionNumbers: questionNumbers,
      usageCount: questionNumbers.length,
      id: listInfo.id,
    };
  });
  
  // FOURTH: Separate lists for reporting
  const singleQuestionLists = allLists.filter(list => list.usageCount === 1);
  const multiQuestionLists = allLists.filter(list => list.usageCount > 1);
  
  return {
    allLists,
    singleQuestionLists,
    multiQuestionLists,
    listsCreated,
    listsUpdated,
    summary: {
      total: allLists.length,
      multiQuestion: multiQuestionLists.length,
      singleQuestion: singleQuestionLists.length,
      created: listsCreated,
      updated: listsUpdated
    }
  };
};